import drawHeroRestMask from '../utils/heroRestMask';
import { VERTEX_SHADER, FRAGMENT_SHADER } from '../utils/heroDivideShaders';

/** Desktop entrance for 1–3 stepped hero panels. The DOM remains the source
 * of layout, accessible content and the final frame. Mobile and reduced motion
 * allocate no GPU. Resize, focus or context loss immediately reveal the DOM.
 * data-hero-panel: coloured boxes; data-hero-content: copy inside them;
 * data-hero-reveal: the cutout photo. The canvas is disposed after handoff,
 * not left rendering a static hero.
 *
 * Layering: the canvas sits UNDER the panels and draws only what
 * the DOM cannot, the meniscus where neighbouring panels melt together. The
 * panels themselves are visible throughout, clipped each frame to the same
 * rounded box the shader draws, so their copy can arrive while they are still
 * budding and the cutout keeps its place between panel 1 and panels 2–3.
 *
 * Plain WebGL, no three.js: one full-screen triangle pair, one program and
 * one texture. That saves a ~156KB download before the entrance can start.
 */

// Choreography, in ms from the first frame. Panels bud in order with a
// back-out settle; each panel's copy rises in as that panel lands; the cutout
// settles alongside rather than after. Tune here, not in the frame code.
const PANEL_STAGGER = 180;
const PANEL_DURATION = 500;
// After the last panel lands, the shader's meniscus converges to the exact
// DOM silhouette (the connector SVGs) over this long, then hands off. Not
// before it lands: blending fades whatever overshoots the rest boxes, which
// reads as a ghost edge beside the already-opaque DOM ground.
const REST_BLEND = 200;
const CONTENT_DELAY = 220; // after its own panel starts
const CONTENT_STAGGER = 80; // between copy blocks in the same panel
const CONTENT_DURATION = 450;
const CONTENT_RISE = 8; // px
const REVEAL_DELAY = 260;
const REVEAL_DURATION = 700;
const REVEAL_DROP = 10; // px
const REVEAL_SCALE = 0.035;
// Half-size of the square a panel buds from.
const SEED = 24;

const clamp01 = value => Math.max(0, Math.min(1, value));
// Same budding curve as the original study: back-out, overshoot then settle.
const backOut = progress => {
	const t = progress - 1;
	return 1 + 2.35 * t ** 3 + 1.35 * t ** 2;
};
// easeOutQuint, the site's house curve (--ease-out-quint in style.css).
const easeOutQuint = progress => 1 - (1 - progress) ** 5;

const parseRgb = css => {
	const [r = 0, g = 0, b = 0] = (css.match(/[\d.]+/g) || []).map(Number);
	return [r / 255, g / 255, b / 255];
};

/** One program drawing a full-screen quad. Throws when anything fails to
 * compile or link, so the caller can fall back to the real hero. */
function createRenderer(canvas, mask) {
	const gl = canvas.getContext('webgl', {
		alpha: true,
		antialias: false,
		premultipliedAlpha: true,
		powerPreference: 'low-power',
	});
	if (!gl) throw new Error('WebGL unavailable');
	const shaders = [
		[gl.VERTEX_SHADER, VERTEX_SHADER],
		[gl.FRAGMENT_SHADER, FRAGMENT_SHADER],
	].map(([type, source]) => {
		const shader = gl.createShader(type);
		gl.shaderSource(shader, source);
		gl.compileShader(shader);
		if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS))
			throw new Error('Hero shader failed');
		return shader;
	});
	const program = gl.createProgram();
	shaders.forEach(shader => gl.attachShader(program, shader));
	gl.linkProgram(program);
	if (!gl.getProgramParameter(program, gl.LINK_STATUS))
		throw new Error('Hero shader failed');
	gl.useProgram(program);

	const buffer = gl.createBuffer();
	gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
	gl.bufferData(
		gl.ARRAY_BUFFER,
		new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
		gl.STATIC_DRAW
	);
	const position = gl.getAttribLocation(program, 'aPosition');
	gl.enableVertexAttribArray(position);
	gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

	// The rest mask only carries alpha. Flipped on upload so vUv (bottom-left
	// origin) samples it the right way up; CLAMP + LINEAR suit a NPOT texture.
	const texture = gl.createTexture();
	gl.bindTexture(gl.TEXTURE_2D, texture);
	gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
	gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, mask);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

	const at = name => gl.getUniformLocation(program, name);
	const locations = Object.fromEntries(
		[
			'uArtboard',
			'uScale',
			'uK',
			'uRadius',
			'uColour',
			'uCentre',
			'uHalf',
			'uOn',
			'uRestMask',
			'uRestMix',
		].map(name => [name, at(name)])
	);
	gl.uniform1i(locations.uRestMask, 0);
	gl.viewport(0, 0, canvas.width, canvas.height);
	gl.clearColor(0, 0, 0, 0);

	return {
		render(u) {
			gl.uniform2f(locations.uArtboard, u.artboard[0], u.artboard[1]);
			gl.uniform1f(locations.uScale, u.scale);
			gl.uniform1f(locations.uK, u.k);
			gl.uniform1f(locations.uRadius, u.radius);
			gl.uniform3fv(locations.uColour, u.colour);
			gl.uniform2fv(locations.uCentre, u.centre);
			gl.uniform2fv(locations.uHalf, u.half);
			gl.uniform1fv(locations.uOn, u.on);
			gl.uniform1f(locations.uRestMix, u.restMix);
			gl.clear(gl.COLOR_BUFFER_BIT);
			gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
		},
		dispose() {
			gl.deleteTexture(texture);
			gl.deleteBuffer(buffer);
			shaders.forEach(shader => gl.deleteShader(shader));
			gl.deleteProgram(program);
			gl.getExtension('WEBGL_lose_context')?.loseContext();
		},
	};
}

async function initialize(node, isCancelled) {
	const desktop = window.matchMedia('(min-width: 1024px)');
	const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
	const publish = state => {
		node.setAttribute('data-hero-motion', state);
		node.dispatchEvent(new window.Event('hero:ready'));
	};
	const expired = () => node.dataset.heroMotion === 'expired';
	if (expired() || !desktop.matches || reduced.matches) {
		publish('static');
		return () => {};
	}
	if (node.dataset.heroMotion !== 'pending') publish('static');

	// Panel geometry depends on the webfonts; nothing else is waited for.
	try {
		await document.fonts?.ready;
	} catch {
		publish('fallback');
		return () => {};
	}
	if (
		isCancelled() ||
		expired() ||
		!node.isConnected ||
		!desktop.matches ||
		reduced.matches
	) {
		publish('static');
		return () => {};
	}

	const panels = [...node.querySelectorAll('[data-hero-panel]')];
	const bounds = node.getBoundingClientRect();
	const boxes = panels.map(panel => {
		const r = panel.getBoundingClientRect();
		return {
			cx: r.left - bounds.left + r.width / 2,
			cy: r.top - bounds.top + r.height / 2,
			hx: r.width / 2,
			hy: r.height / 2,
		};
	});
	if (
		!boxes.length ||
		boxes.length > 3 ||
		!bounds.width ||
		boxes.some(b => !b.hx || !b.hy)
	) {
		publish('static');
		return () => {};
	}

	const radius =
		parseFloat(window.getComputedStyle(panels[0]).borderTopLeftRadius) || 18;
	const scale = Math.min(window.devicePixelRatio || 1, 2);
	const uniforms = {
		artboard: [bounds.width, bounds.height],
		scale,
		k: radius * (40.75 / 18),
		radius,
		colour: parseRgb(window.getComputedStyle(panels[0]).backgroundColor),
		centre: new Float32Array(6),
		half: new Float32Array(6),
		on: new Float32Array(3),
		restMix: 0,
	};

	const canvas = document.createElement('canvas');
	canvas.width = Math.floor(bounds.width * scale);
	canvas.height = Math.floor(bounds.height * scale);
	let renderer;
	try {
		const mask = document.createElement('canvas');
		mask.width = Math.ceil(bounds.width * scale);
		mask.height = Math.ceil(bounds.height * scale);
		const context = mask.getContext('2d');
		if (!context) throw new Error('Canvas 2D unavailable');
		drawHeroRestMask(context, node, panels, bounds, scale);
		renderer = createRenderer(canvas, mask);
	} catch {
		publish('fallback');
		return () => {};
	}
	canvas.setAttribute('aria-hidden', 'true');
	// No z-index: inserted just before the panels' container, tree order paints
	// it over the photograph and under every panel, whether the panel has a
	// z-index (split hero: 1 and 3, around the cutout's 2) or none (product,
	// general and case study heroes). A z-index here would cover the latter.
	canvas.style.cssText =
		'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;';
	const anchor = [...node.children].find(child => child.contains(panels[0]));

	// Each copy block starts CONTENT_DELAY after its own panel, CONTENT_STAGGER
	// after the block before it in that panel.
	const perPanel = new Map();
	// Undisplayed copy (a breakpoint-only link) is left alone and takes no slot.
	const content = [...node.querySelectorAll('[data-hero-content]')]
		.filter(el => window.getComputedStyle(el).display !== 'none')
		.map(el => {
			const i = panels.indexOf(el.closest('[data-hero-panel]'));
			const j = perPanel.get(i) ?? 0;
			perPanel.set(i, j + 1);
			// `top` only moves a relatively positioned box, and works on inline
			// spans where a transform would not.
			const rises = window.getComputedStyle(el).position === 'static';
			return {
				el,
				at:
					Math.max(0, i) * PANEL_STAGGER + CONTENT_DELAY + j * CONTENT_STAGGER,
				rises,
				opacity: el.style.opacity,
				position: el.style.position,
				top: el.style.top,
			};
		});
	const reveal = [...node.querySelectorAll('[data-hero-reveal]')].map(el => ({
		el,
		opacity: el.style.opacity,
		transform: el.style.transform,
	}));
	const panelOriginals = panels.map(el => ({
		el,
		clipPath: el.style.clipPath,
	}));

	const lastPanelEnd = (boxes.length - 1) * PANEL_STAGGER + PANEL_DURATION;
	const handoffAt = lastPanelEnd + REST_BLEND;
	const total = Math.max(
		handoffAt,
		...content.map(c => c.at + CONTENT_DURATION),
		reveal.length ? REVEAL_DELAY + REVEAL_DURATION : 0
	);

	let frame = 0;
	let last = null;
	let elapsed = 0;
	let disposed = false;
	let handedOff = false;
	let visible = true;
	let ro;
	let io;
	const stop = () => {
		cancelAnimationFrame(frame);
		frame = 0;
		last = null;
	};
	const restore = () => {
		for (const { el, clipPath } of panelOriginals) el.style.clipPath = clipPath;
		for (const c of content) {
			c.el.style.opacity = c.opacity;
			c.el.style.position = c.position;
			c.el.style.top = c.top;
		}
		for (const r of reveal) {
			r.el.style.opacity = r.opacity;
			r.el.style.transform = r.transform;
		}
	};
	const finish = (state = 'complete') => {
		if (disposed) return;
		disposed = true;
		stop();
		ro?.disconnect();
		io?.disconnect();
		desktop.removeEventListener('change', cancel);
		reduced.removeEventListener('change', cancel);
		document.removeEventListener('visibilitychange', sync);
		node.removeEventListener('focusin', cancel);
		canvas.removeEventListener('webglcontextlost', lost);
		restore();
		renderer.dispose();
		canvas.remove();
		publish(state);
	};
	const cancel = () => finish('static');
	const lost = event => {
		event.preventDefault();
		finish('fallback');
	};

	// Shader boxes for this instant, mirrored onto the panels as clip-paths.
	const draw = time => {
		boxes.forEach((box, i) => {
			const start = i * PANEL_STAGGER;
			const ease = backOut(clamp01((time - start) / PANEL_DURATION));
			const parent = boxes[Math.max(0, i - 1)];
			const seedX = i
				? parent.cx + Math.sign(box.cx - parent.cx) * (parent.hx - SEED)
				: box.cx;
			const seedY = i
				? parent.cy + Math.sign(box.cy - parent.cy) * (parent.hy - SEED)
				: box.cy;
			const cx = seedX + (box.cx - seedX) * ease;
			const cy = seedY + (box.cy - seedY) * ease;
			const hx = SEED + (box.hx - SEED) * ease;
			const hy = SEED + (box.hy - SEED) * ease;
			const on = time >= start;
			uniforms.on[i] = on ? 1 : 0;
			uniforms.centre.set([cx, cy], i * 2);
			uniforms.half.set([hx, hy], i * 2);
			// inset() takes negative lengths, so the overshoot stays drawn.
			panels[i].style.clipPath = on
				? `inset(${cy - hy - (box.cy - box.hy)}px ${box.cx + box.hx - (cx + hx)}px ${
						box.cy + box.hy - (cy + hy)
					}px ${cx - hx - (box.cx - box.hx)}px round ${Math.min(radius, hx, hy)}px)`
				: 'inset(50%)';
		});
		uniforms.restMix = clamp01((time - lastPanelEnd) / REST_BLEND);
		renderer.render(uniforms);
	};
	const animateCopy = time => {
		for (const c of content) {
			const p = easeOutQuint(clamp01((time - c.at) / CONTENT_DURATION));
			c.el.style.opacity = String(
				p * (c.opacity === '' ? 1 : Number(c.opacity))
			);
			if (c.rises) c.el.style.top = `${(1 - p) * CONTENT_RISE}px`;
		}
		const p = easeOutQuint(clamp01((time - REVEAL_DELAY) / REVEAL_DURATION));
		for (const r of reveal) {
			r.el.style.opacity = String(
				p * (r.opacity === '' ? 1 : Number(r.opacity))
			);
			r.el.style.transform = `translateY(${(1 - p) * REVEAL_DROP}px) scale(${
				1 + (1 - p) * REVEAL_SCALE
			})`;
		}
	};
	const tick = time => {
		if (disposed) return;
		if (last !== null) elapsed += time - last;
		last = time;
		if (elapsed >= total) {
			finish();
			return;
		}
		if (!handedOff) {
			try {
				// The converged frame is drawn at the handoff, as before the
				// rewrite, so the hero-mask probe can compare it to the DOM mask.
				draw(Math.min(elapsed, handoffAt));
			} catch {
				finish('fallback');
				return;
			}
		}
		if (elapsed >= handoffAt && !handedOff) {
			// The panels are opaque throughout, so the handoff is one frame:
			// unclip them (the connectors reappear) and stop drawing.
			handedOff = true;
			for (const { el, clipPath } of panelOriginals)
				el.style.clipPath = clipPath;
			canvas.style.visibility = 'hidden';
		}
		animateCopy(elapsed);
		frame = requestAnimationFrame(tick);
	};
	function sync() {
		if (disposed) return;
		if (!visible || document.hidden) stop();
		else if (!frame) frame = requestAnimationFrame(tick);
	}
	try {
		for (const c of content) if (c.rises) c.el.style.position = 'relative';
		draw(0);
		animateCopy(0);
	} catch {
		restore();
		renderer.dispose();
		publish('fallback');
		return () => {};
	}
	node.insertBefore(canvas, anchor);
	publish('running');
	canvas.addEventListener('webglcontextlost', lost);
	node.addEventListener('focusin', cancel);
	desktop.addEventListener('change', cancel);
	reduced.addEventListener('change', cancel);
	document.addEventListener('visibilitychange', sync);
	ro = new ResizeObserver(() => {
		const current = node.getBoundingClientRect();
		if (current.width !== bounds.width || current.height !== bounds.height)
			cancel();
	});
	ro.observe(node);
	io = new IntersectionObserver(entries => {
		visible = entries[0].isIntersecting;
		sync();
	});
	io.observe(node);
	sync();
	return () => finish('static');
}

// Core registers only synchronous return values as teardown callbacks.
function HeroDivide({ node }) {
	let cancelled = false;
	let dispose = () => {};
	initialize(node, () => cancelled)
		.then(cleanup => {
			if (cancelled) cleanup();
			else dispose = cleanup;
		})
		.catch(() => {
			// Unexpected initialization failure must release the early loading state.
			node.setAttribute('data-hero-motion', 'fallback');
			node.dispatchEvent(new window.Event('hero:ready'));
		});
	return () => {
		cancelled = true;
		dispose();
	};
}

export default HeroDivide;
